import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronDown, ChevronRight, Menu, LayoutGrid } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useHeaderFooter } from "@/lib/headerFooter";

let cache = null;
const useMenu = () => {
  const [m, setM] = useState(cache);
  useEffect(() => { if (!cache) api.get("/site/menu").then((r) => { cache = r.data; setM(r.data); }).catch(() => {}); }, []);
  return m;
};
const A = ({ to, children, ...p }) => (to?.startsWith("http") ? <a href={to} target="_blank" rel="noreferrer" {...p}>{children}</a> : <Link to={to || "#"} {...p}>{children}</Link>);

function Panel({ item, onClose }) {
  const kids = item.children;
  return (
    <div className="absolute left-0 right-0 top-full bg-white text-foreground shadow-2xl border-t-2 border-accent z-40 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`mega-panel-${item.id}`}>
      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-[1fr_220px] gap-8">
        <div className="columns-2 md:columns-3 xl:columns-4 gap-6 max-h-[60vh] overflow-y-auto">
          {kids.map((c) => (
            <A key={c.id} to={c.link} onClick={onClose} className="block break-inside-avoid py-1.5 text-sm text-slate-700 hover:text-accent hover:translate-x-0.5 transition-[color,transform]" data-testid={`mega-link-${c.id}`}>{c.label}</A>))}
        </div>
        <div className="border-l pl-6 space-y-2">
          <div className="text-[11px] font-mono tracking-[0.2em] text-muted-foreground">{item.label.toUpperCase()}</div>
          {item.link && <A to={item.link} onClick={onClose} className="block font-semibold text-steel hover:text-accent">Shop all {item.label} →</A>}
          {item.featured.map((f) => <A key={f.id} to={f.link} onClick={onClose} className="block text-sm font-semibold text-accent hover:underline" data-testid={`mega-featured-${f.id}`}>{f.label}</A>)}
        </div>
      </div>
    </div>
  );
}

export function MegaMenuBar() {
  const menu = useMenu();
  const [open, setOpen] = useState(null);
  const t = useRef(null);
  if (!menu?.enabled || !menu.items.length) return null;
  const enter = (id) => { clearTimeout(t.current); t.current = setTimeout(() => setOpen(id), 120); };
  const leave = () => { clearTimeout(t.current); t.current = setTimeout(() => setOpen(null), 150); };
  return (
    <nav className="hidden lg:block relative border-t border-steel-light" data-testid="mega-menu" onMouseLeave={leave}>
      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 h-11 overflow-x-auto [scrollbar-width:none]">
      <div className="flex items-center gap-0.5 w-max mx-auto">
        {menu.items.map((i) => (
          <div key={i.id} onMouseEnter={() => (i.children.length ? enter(i.id) : setOpen(null))}>
            {i.children.length ? (
              <button onClick={() => setOpen(open === i.id ? null : i.id)} className={`flex items-center gap-1 px-2 xl:px-2.5 h-11 text-[13px] xl:text-sm font-medium whitespace-nowrap transition-colors ${open === i.id ? "text-accent" : "text-slate-200 hover:text-accent"}`} data-testid={`mega-item-${i.id}`}>
                {i.label} <ChevronDown size={14} className={`transition-transform ${open === i.id ? "rotate-180" : ""}`} /></button>
            ) : <A to={i.link} className="block px-2 xl:px-2.5 leading-[2.75rem] text-[13px] xl:text-sm font-medium text-slate-200 hover:text-accent whitespace-nowrap" data-testid={`mega-item-${i.id}`}>{i.label}</A>}
          </div>))}
      </div>
      </div>
      {open && menu.items.find((i) => i.id === open) && (
        <div onMouseEnter={() => clearTimeout(t.current)}><Panel item={menu.items.find((i) => i.id === open)} onClose={() => setOpen(null)} /></div>)}
    </nav>
  );
}

export function MegaMenuMobile() {
  const menu = useMenu();
  const [open, setOpen] = useState(false);
  const [exp, setExp] = useState(null);
  const hf = useHeaderFooter();
  if (!menu?.enabled || !menu.items.length) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} aria-label="Menu" data-testid="mega-mobile-toggle"
        className="lg:hidden grid place-items-center w-10 h-10 rounded-md bg-steel-light hover:bg-accent hover:text-steel transition-colors"><Menu size={20} /></button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[85%] sm:max-w-sm p-0 overflow-y-auto" data-testid="mega-mobile-sheet">
          <SheetHeader className="px-5 py-4 border-b"><SheetTitle className="flex items-center gap-2"><LayoutGrid size={18} /> Shop by Category</SheetTitle></SheetHeader>
          <div className="divide-y">
            {menu.items.map((i) => (
              <div key={i.id}>
                {i.children.length ? (
                  <button onClick={() => setExp(exp === i.id ? null : i.id)} className="w-full flex items-center justify-between px-5 py-3.5 font-semibold text-steel" data-testid={`mega-mobile-item-${i.id}`}>
                    {i.label} <ChevronRight size={16} className={`transition-transform ${exp === i.id ? "rotate-90" : ""}`} /></button>
                ) : <A to={i.link} onClick={() => setOpen(false)} className="block px-5 py-3.5 font-semibold text-steel">{i.label}</A>}
                {exp === i.id && (
                  <div className="bg-slate-50 pb-2">
                    {[...(i.link ? [{ id: "all", label: `Shop all ${i.label}`, link: i.link }] : []), ...i.featured, ...i.children].map((c) => (
                      <A key={c.id} to={c.link} onClick={() => setOpen(false)} className="block px-8 py-2 text-sm text-slate-700 hover:text-accent">{c.label}</A>))}
                  </div>)}
              </div>))}
          </div>
          {hf?.header?.links?.length > 0 && (
            <div className="border-t bg-slate-50 py-2" data-testid="mega-mobile-header-links">
              {hf.header.links.map((l) => <A key={l.id} to={l.link} onClick={() => setOpen(false)} className="block px-5 py-2.5 text-sm font-medium text-steel hover:text-accent">{l.label}</A>)}
            </div>)}
          {hf?.header?.phone && <a href={`tel:${hf.header.phone.replace(/\s/g, "")}`} className="block px-5 py-3 text-sm font-semibold text-accent border-t">Call {hf.header.phone}</a>}
        </SheetContent>
      </Sheet>
    </>
  );
}
